import axios from 'axios'

// Istanza Axios unica (cookie httpOnly inviati con withCredentials).
const api = axios.create({
	baseURL: import.meta.env.VITE_API_URL,
	withCredentials: true
})

// L'AuthProvider registra qui il proprio handler di "sessione non valida".
let onUnauthorized = null;
export function setUnauthorizedHandler(fn)
{
	onUnauthorized = fn;
}

// Notifica "sessione non recuperabile" (usata anche dal client SSE): porta l'app in anon.
export function notifyUnauthorized()
{
	if (onUnauthorized) onUnauthorized();
}

// Il backend puo' essere temporaneamente irraggiungibile per un riavvio: nodemon in dev
// (~1-2s) o un redeploy/restart del container Docker in produzione (puo' richiedere
// diversi secondi in piu'). In quel caso (nessuna response HTTP: errore di rete, non un
// 401) si ritenta invece di considerare la sessione persa.
// NB: il timeout va SOLO qui, non sull'istanza: la stessa istanza serve gli upload
// lunghi (PDF volantino, referenze massive, excel), che un timeout globale taglierebbe.
// Senza limite, invece, un /refresh appeso bloccherebbe il single-flight a tempo
// indefinito e con esso ogni richiesta in attesa di rinnovo.
async function postRefresh(retriesLeft = 6)
{
	try
	{
		return await api.post("/api/auth/refresh", null, {timeout: 15000});
	}
	catch (e)
	{
		if (!e?.response && retriesLeft > 0)
		{
			await new Promise((resolve) => setTimeout(resolve, 1000));
			return postRefresh(retriesLeft - 1);
		}
		throw e;
	}
}

// Refresh SINGLE-FLIGHT condiviso: piu' 401 concorrenti — di Axios E del client SSE —
// condividono UNA sola chiamata a /refresh. Fondamentale: chiamate concorrenti a
// /refresh presenterebbero refresh-token gia' ruotati → reuse-detection → revoca family.
let refreshing = null;
export function refreshSession()
{
	if (!refreshing)
		refreshing = postRefresh().finally(() => { refreshing = null; });
	return refreshing;
}

/**
 * Verifica leggera della sessione, usata dal client SSE quando una connessione cade.
 * EventSource non espone lo status HTTP dell'errore: invece di rinnovare alla cieca
 * (ogni rinnovo e' una ROTAZIONE del refresh token, quindi una finestra di rischio)
 * si fa una GET che passa dall'interceptor qui sotto. Se l'access e' valido non
 * succede nulla; se e' scaduto/ruotato il rinnovo parte da li'; se la sessione e'
 * davvero morta la promise rigetta con 401 e il chiamante smette di riconnettere.
 * Single-flight: piu' connessioni SSE che cadono insieme fanno UNA sola verifica.
 */
let probing = null;
export function probeSession()
{
	if (!probing)
		probing = api.get("/api/auth/me").finally(() => { probing = null; });
	return probing;
}

// 401 dai quali si puo' rientrare con un solo /refresh, senza disturbare l'utente.
const RECOVERABLE_ERRORS = new Set(["token_expired", "unauthorized", "session_revoked"]);

function isAuthEndpoint(url = "")
{
	return url.includes("/api/auth/login") || url.includes("/api/auth/refresh") || url.includes("/api/auth/logout");
}

api.interceptors.response.use(
	(res) => res,
	async (error) =>
	{
		const response = error?.response;
		const config = error?.config;
		if (!response || !config || isAuthEndpoint(config.url))
			return Promise.reject(error);

		// Access scaduto (token_expired), cookie access assente perche' gia' rimosso dal
		// browser (unauthorized), oppure sessione ruotata da un rinnovo appena andato a
		// buon fine mentre questa richiesta era in volo (session_revoked) → tenta UN
		// refresh (single-flight) e riprova la richiesta.
		// account_disabled / refresh_invalid NON sono recuperabili: cadono nel logout sotto.
		const recoverable = RECOVERABLE_ERRORS.has(response.data?.error);
		if (response.status === 401 && recoverable && !config.__retried)
		{
			config.__retried = true;
			try
			{
				await refreshSession();
				return api(config);
			}
			catch (e)
			{
				// Il refresh e' fallito: logout globale SOLO se e' un 401 vero (sessione
				// morta). Rete irraggiungibile dopo i retry, 403 CSRF, 5xx ecc. non
				// significano necessariamente sessione persa — fallisce solo questa
				// richiesta, senza buttare fuori l'utente dalle altre schede/azioni.
				if (e?.response?.status === 401)
					notifyUnauthorized();
				return Promise.reject(e);
			}
		}

		// Altri 401 (unauthorized / account_disabled / refresh_invalid) → logout.
		if (response.status === 401)
		{
			notifyUnauthorized();
		}

		return Promise.reject(error);
	}
);

export default api
